/**
 * ConversationInboxList — compact inbox-style rows for conversation browse (BUB-135).
 * Avoids wide table columns; tap a row to open lead or chat drawer.
 */

import React from "react";
import {
  Avatar,
  Button,
  Empty,
  Flex,
  List,
  Pagination,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  PushpinFilled,
  PushpinOutlined,
  RightOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { format, formatDistanceToNow } from "date-fns";
import {
  getConversationInboxStatusTag,
  getConversationHandoffStatusTag,
  getConversationLeadStatusTag,
} from "../utils/conversationLeadStatus.js";

const { Text } = Typography;

export function getConversationRowKey(record) {
  return record.customerPhone || record.leadId || record.referenceNumber;
}

function formatRelativeActivity(value) {
  if (!value) return "—";
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return "—";
  }
}

function formatFullActivity(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return "—";
  }
}

function buildStatusTags(record) {
  const tags = [];
  const inboxTag = getConversationInboxStatusTag(record);
  if (inboxTag) tags.push(inboxTag);
  const handoffTag = getConversationHandoffStatusTag(record);
  if (handoffTag) tags.push(handoffTag);
  if (record.needsAttention) {
    tags.push({
      key: "attention",
      label: "Attention",
      color: "red",
      icon: <WarningOutlined />,
    });
  }
  if (record.flowStatus || record.status) {
    tags.push({
      key: "flow-status",
      label: record.flowStatus || record.status,
    });
  }
  tags.push(getConversationLeadStatusTag(record));
  if (record.source) {
    tags.push({ key: "source", label: record.source, color: "blue" });
  }
  if (record.messageCount != null) {
    tags.push({
      key: "msgs",
      label: `${record.messageCount} msgs`,
      color: "default",
    });
  }
  return tags;
}

function ConversationInboxItem({
  record,
  onOpen,
  onOpenLead,
  onTogglePin,
  isPinning = false,
}) {
  const displayName = record.customerName || record.customerPhone || "Unknown";
  const subtitle =
    record.customerName && record.customerPhone ? record.customerPhone : null;
  const statusTags = buildStatusTags(record);
  const visibleTags = statusTags.slice(0, 2);
  const hiddenTags = statusTags.slice(2);
  const isPinned = record.isPinned === true;

  return (
    <List.Item
      onClick={() => onOpen(record)}
      style={{
        cursor: "pointer",
        padding: "12px 16px",
        backgroundColor: record.needsAttention
          ? "#fff7f6"
          : isPinned
            ? "#f6fffa"
            : record.draftReadyToPromote && !record.leadId
              ? "#f6ffed"
              : undefined,
        borderRadius: 8,
        marginBottom: 4,
        border: isPinned ? "1px solid #b7eb8f" : "1px solid #f0f0f0",
      }}
    >
      <Flex align="flex-start" gap={12} style={{ width: "100%", minWidth: 0 }}>
        <Avatar
          icon={<UserOutlined />}
          style={{
            backgroundColor: record.needsAttention ? "#ff4d4f" : "#005249",
            flexShrink: 0,
          }}
        />
        <Flex vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Flex align="center" justify="space-between" gap={8}>
            <Flex align="center" gap={6} style={{ flex: 1, minWidth: 0 }}>
              {isPinned && (
                <PushpinFilled style={{ color: "#389e0d", flexShrink: 0 }} />
              )}
              <Text strong ellipsis style={{ flex: 1 }}>
                {displayName}
              </Text>
            </Flex>
            <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
              {onTogglePin && record.customerPhone && (
                <Tooltip title={isPinned ? "Unpin" : "Pin"}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={isPinned ? "Unpin conversation" : "Pin conversation"}
                    icon={
                      isPinned ? (
                        <PushpinFilled style={{ color: "#389e0d" }} />
                      ) : (
                        <PushpinOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    loading={isPinning}
                    onClick={(event) => {
                      event.stopPropagation();
                      onTogglePin(record);
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title={formatFullActivity(record.lastActivityAt)}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, whiteSpace: "nowrap" }}
                >
                  {formatRelativeActivity(record.lastActivityAt)}
                </Text>
              </Tooltip>
            </Flex>
          </Flex>
          {subtitle && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {subtitle}
            </Text>
          )}
          {record.referenceNumber && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record.referenceNumber}
            </Text>
          )}
          <Text
            type={record.preview ? "secondary" : undefined}
            ellipsis
            style={{ fontSize: 13 }}
          >
            {record.preview || "No preview available"}
          </Text>
          {statusTags.length > 0 && (
            <Space size={4} wrap>
              {visibleTags.map((tag) => (
                <Tag
                  key={tag.key}
                  color={tag.color}
                  icon={tag.icon}
                  style={{ margin: 0 }}
                >
                  {tag.label}
                </Tag>
              ))}
              {record.leadId && onOpenLead && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: "auto" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenLead(record);
                  }}
                >
                  Open lead
                </Button>
              )}
              {hiddenTags.length > 0 && (
                <Tooltip title={hiddenTags.map((t) => t.label).join(", ")}>
                  <Tag style={{ margin: 0 }}>+{hiddenTags.length}</Tag>
                </Tooltip>
              )}
            </Space>
          )}
        </Flex>
        <RightOutlined
          style={{ color: "#bfbfbf", flexShrink: 0, marginTop: 4 }}
        />
      </Flex>
    </List.Item>
  );
}

const ConversationInboxList = ({
  conversations = [],
  loading = false,
  pagination,
  onOpen,
  onOpenLead,
  onTogglePin,
  pinningChannelId = null,
  onPageChange,
}) => {
  if (!loading && conversations.length === 0) {
    return <Empty description="No conversations found" />;
  }

  return (
    <Flex vertical gap={8}>
      <List
        loading={loading}
        dataSource={conversations}
        rowKey={getConversationRowKey}
        renderItem={(record) => (
          <ConversationInboxItem
            record={record}
            onOpen={onOpen}
            onOpenLead={onOpenLead}
            onTogglePin={onTogglePin}
            isPinning={pinningChannelId === record.customerPhone}
          />
        )}
        split={false}
      />
      {pagination.total > 0 && (
        <Flex justify="flex-end">
          <Pagination
            current={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            pageSizeOptions={["10", "25", "50", "100"]}
            onChange={onPageChange}
            showTotal={(total) => `${total} conversations`}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default ConversationInboxList;
